import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Cpu, Database, Tractor, ShieldCheck, Leaf } from 'lucide-react';

export const HeroBanner = () => {
  const highlights = [
    {
      icon: <Leaf size={26} className="text-[#F3C72C]" />,
      title: 'Fundo for Farmers',
      desc: 'A digital operating system for your farm — manage crops, livestock, and finances effortlessly.',
      external: true,
      link: 'https://fundo.ashita.africa/',
      color: 'from-[#F3C72C]/10 to-transparent',
    },
    {
      icon: <Database size={26} className="text-[#F3C72C]" />,
      title: 'Ashita CMS Services',
      desc: 'Request new features anytime. We deploy them without extra fees — fully managed by Ashita.',
      external: false,
      link: '/cms-services',
      color: 'from-[#F3C72C]/10 to-transparent',
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[#0b0b0b] py-24 md:py-32 text-white">
      {/* Soft Animated Background Glow */}
      <motion.div
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(243,199,44,0.12),transparent_65%)]"
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ repeat: Infinity, duration: 12, ease: 'easeInOut' }}
      />

      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-12 px-6 md:px-24 z-10">
        {/* HERO LEFT */}
        <div className="flex flex-col justify-center space-y-6">
          <motion.h1
            className="text-5xl md:text-7xl font-extrabold leading-tight"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            Elevating <span className="text-[#F3C72C]">African Agriculture</span> through Digital
            Innovation.
          </motion.h1>

          <motion.p
            className="text-gray-300 text-lg md:text-xl max-w-xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.9 }}
          >
            Building intelligent farm software, secure IoT ecosystems, and next-generation digital
            tools shaping the future of agritech in Africa.
          </motion.p>

          {/* CTA */}
          <motion.div
            className="flex flex-wrap gap-4 mt-5"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.9 }}
          >
            <Link
              to="/contact-us"
              className="px-6 py-3 rounded-xl bg-black text-white border border-[#F3C72C] font-semibold hover:bg-[#F3C72C]/10 transition-all shadow-lg"
            >
              Get Started
            </Link>
          </motion.div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-8 mt-10">
            <motion.div whileHover={{ scale: 1.05 }} className="flex items-center gap-3">
              <Tractor className="text-[#F3C72C]" size={26} />
              <div>
                <p className="font-bold text-xl">10+</p>
                <p className="text-gray-400 text-sm">Farms Digitized</p>
              </div>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} className="flex items-center gap-3">
              <ShieldCheck className="text-[#F3C72C]" size={26} />
              <div>
                <p className="font-bold text-xl">Secure Systems</p>
                <p className="text-gray-400 text-sm">Enterprise Architecture</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* HERO RIGHT — HIGHLIGHTS */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 mt-8 md:mt-0"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {highlights.map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className={`p-6 rounded-2xl bg-gradient-to-br ${item.color} border border-[#F3C72C]/20 backdrop-blur-xl shadow-lg`}
            >
              <div className="flex items-center gap-3 mb-3">
                {item.icon}
                <h3 className="text-lg font-bold">{item.title}</h3>
              </div>

              <p className="text-gray-300 text-sm mb-4">{item.desc}</p>

              {item.external ? (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F3C72C] font-semibold hover:underline text-sm"
                >
                  Learn More →
                </a>
              ) : (
                <Link
                  to={item.link}
                  className="text-[#F3C72C] font-semibold hover:underline text-sm"
                >
                  Learn More →
                </Link>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
